/**
 * Live Excel Orchestrator
 *
 * Top-level orchestrator for Excel editing operations.
 * Adapted to the fat AgentBaseClass pattern with proper lifecycle hooks.
 */
import { AgentBaseClass } from "../../agents/base/agentBaseClass";
import { ReadExcelTool } from "../tools/readExcel/readExcel.tool";
import { EditContentTool } from "../agents/editContent/tools/editContent/editContent.tool";
import { EditFormatTool } from "../agents/editFormat/tools/editFormat/editFormat.tool";
import { EditChartTool } from "../agents/editChart/tools/editChart/editChart.tool";
import { EditStructureTool } from "../tools/editStructure/editStructure.tool";
import { AlphaSenseSearchAgent, ExternalFileSearchAgent, ImageSearchAgent, } from "../agents/search";
import { EXCEL_PROMPT_SECTIONS } from "../../promptEnv/excel";
import { createPromptGetTool, createPromptSearchTool } from "../../promptEnv/tools";
import { buildExcelOrchestratorSystemPrompt } from "./prompt";
function asRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function summarizeLoopGuardExcelResults(value) {
    const lines = [];
    const root = asRecord(value);
    const buckets = asRecord(root.results || root.data);
    const listSheets = Array.isArray(buckets.listSheets) ? buckets.listSheets : [];
    if (listSheets.length) {
        const names = listSheets
            .map((item) => asRecord(item).sheetName)
            .filter((name) => typeof name === "string" && name.trim().length > 0)
            .slice(0, 8);
        if (names.length) {
            lines.push(`Sheets: ${names.join(", ")}`);
        }
    }
    const readContent = Array.isArray(buckets.readContent) ? buckets.readContent : [];
    if (readContent.length) {
        const first = asRecord(readContent[0]);
        const sheet = typeof first.sheetName === "string" && first.sheetName ? first.sheetName : "(active)";
        const range = typeof first.targetRange === "string" && first.targetRange
            ? first.targetRange
            : "(used range)";
        const rows = typeof first.rowCount === "number" ? String(first.rowCount) : "?";
        const cols = typeof first.columnCount === "number" ? String(first.columnCount) : "?";
        lines.push(`Read content: ${sheet} ${range} (${rows}x${cols})`);
        const sampleValues = Array.isArray(first.sampleValues) ? first.sampleValues : [];
        if (sampleValues.length) {
            const headerRow = Array.isArray(sampleValues[0]) ? sampleValues[0] : [];
            const previewHeader = headerRow
                .map((cell) => (cell === null || cell === undefined ? "" : String(cell)))
                .filter((cell) => cell.trim().length > 0)
                .slice(0, 6);
            if (previewHeader.length) {
                lines.push(`Header preview: ${previewHeader.join(" | ")}`);
            }
            const firstDataRow = Array.isArray(sampleValues[1]) ? sampleValues[1] : [];
            const previewData = firstDataRow
                .map((cell) => (cell === null || cell === undefined ? "" : String(cell)))
                .filter((cell) => cell.trim().length > 0)
                .slice(0, 6);
            if (previewData.length) {
                lines.push(`First row preview: ${previewData.join(" | ")}`);
            }
        }
    }
    const warnings = Array.isArray(root.warnings) ? root.warnings : [];
    if (warnings.length) {
        lines.push(`Warnings: ${warnings[0]}`);
    }
    const errors = Array.isArray(root.errors) ? root.errors : [];
    if (errors.length) {
        lines.push(`Errors: ${errors[0]}`);
    }
    return lines;
}
function buildLoopGuardSummary(requirementResults) {
    const loopGuard = requirementResults.find((item) => item.requirementId.startsWith("loop_guard"));
    if (!loopGuard?.relevantResults) {
        return null;
    }
    const root = asRecord(loopGuard.relevantResults);
    const buckets = asRecord(root.results || root.data);
    const readContent = Array.isArray(buckets.readContent) ? buckets.readContent : [];
    const first = readContent.length ? asRecord(readContent[0]) : {};
    const sheet = typeof first.sheetName === "string" && first.sheetName ? first.sheetName : "active sheet";
    const range = typeof first.targetRange === "string" && first.targetRange ? first.targetRange : "used range";
    const rows = typeof first.rowCount === "number" ? first.rowCount : undefined;
    const cols = typeof first.columnCount === "number" ? first.columnCount : undefined;
    if (rows !== undefined && cols !== undefined) {
        return `Read current workbook successfully. I analyzed ${sheet} ${range} (${rows} rows x ${cols} columns).`;
    }
    return `Read current workbook successfully. I analyzed ${sheet} ${range}.`;
}
function buildAssistantContent(summary, requirementResults) {
    const loopGuardSummary = buildLoopGuardSummary(requirementResults);
    const topSummary = (summary || "").trim() || "Completed.";
    const lines = [
        loopGuardSummary && topSummary.toLowerCase().includes("loop guard")
            ? loopGuardSummary
            : topSummary,
    ];
    if (requirementResults.length) {
        lines.push("", "Requirement outcomes:");
        for (const result of requirementResults) {
            lines.push(`- [${result.requirementId}] ${result.rationale}`);
            if (result.requirementId.startsWith("loop_guard") && result.relevantResults !== undefined) {
                const extracted = summarizeLoopGuardExcelResults(result.relevantResults);
                for (const line of extracted) {
                    lines.push(`  ${line}`);
                }
            }
        }
    }
    return lines.join("\n");
}
export class LiveExcelOrchestrator extends AgentBaseClass {
    parseRequirementsFromMessage(message) {
        const lines = message
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);
        const bulletLines = lines
            .filter((line) => /^[-*]\s+/.test(line) || /^\d+\.\s+/.test(line))
            .map((line) => line.replace(/^([-*]\s+|\d+\.\s+)/, "").trim())
            .filter((line) => line.length > 0);
        if (bulletLines.length >= 2) {
            return bulletLines.slice(0, 8).map((requirement, index) => ({
                id: String.fromCharCode(65 + index),
                requirement,
            }));
        }
        return [{ id: "A", requirement: message.trim() }];
    }
    /**
     * Reminders are pre-injected by the host message service via the shared
     * ReminderCollector + MessagePreparer pipeline.
     */
    getReminderOptions() {
        return {
            includePresentation: false,
            includeSecurityWarning: false,
            includeCustomInstructions: false,
            includeDeckRegistry: false,
            includeExternalFiles: false,
        };
    }
    constructor(runner) {
        super();
        this.agentType = "liveExcelOrchestrator";
        this.name = "liveExcelOrchestrator";
        this.isAgent = true;
        this.useThreadManagement = true;
        this.useTaskTracking = true;
        this.useContextResolution = true;
        this.useSubmitResultsPattern = true;
        // Excel uses workbook checkpointing via excelSessionLifecycleService, not PPT checkpoints.
        this.createsUndoCheckpoint = false;
        this.stream = true;
        this.maxToolLoopIterations = 16;
        this.runner = null;
        this._workbookContext = undefined;
        this._requirements = [];
        this._inputMessage = "";
        if (runner) {
            this.runner = runner;
            this._createTools(runner);
        }
        this.initializeAgent();
    }
    _createTools(runner) {
        const readExcel = new ReadExcelTool(runner);
        const editContent = new EditContentTool(runner);
        const editFormat = new EditFormatTool(runner);
        const editChart = new EditChartTool(runner);
        const editStructure = new EditStructureTool(runner);
        const externalFileSearchAgent = new ExternalFileSearchAgent();
        const searchAlphaSense = new AlphaSenseSearchAgent();
        const imageSearchAgent = new ImageSearchAgent();
        const promptGet = createPromptGetTool(EXCEL_PROMPT_SECTIONS);
        const promptSearch = createPromptSearchTool(EXCEL_PROMPT_SECTIONS);
        // Preserve submitResults tool if already injected
        const submitResults = this.toolCalls.find(t => t.name === 'submitResults');
        this.toolCalls = [
            readExcel,
            editContent, editFormat, editChart, editStructure,
            externalFileSearchAgent, searchAlphaSense, imageSearchAgent,
            promptGet, promptSearch,
        ];
        if (submitResults) {
            this.toolCalls.push(submitResults);
        }
    }
    async beforeExecute(context) {
        const { params } = context;
        // Store state for generateSystemPrompt and buildInitialMessages
        this._workbookContext = params.input.workbookContext;
        this._requirements =
            params.input.requirements && params.input.requirements.length > 0
                ? params.input.requirements
                : this.parseRequirementsFromMessage(params.input.message);
        this._inputMessage = params.input.message;
        // Create/recreate tools with the runner from params
        if (params.runner) {
            this.runner = params.runner;
            this._createTools(params.runner);
        }
        // Set session IDs on all tools
        if (params.sessionId) {
            for (const tool of this.toolCalls) {
                if ('setSessionId' in tool && typeof tool.setSessionId === 'function') {
                    tool.setSessionId(params.sessionId);
                }
            }
        }
        console.log(`[LiveExcelOrchestrator] Starting with ${this._requirements.length} requirements`);
    }
    generateSystemPrompt(_resolvedContext) {
        return buildExcelOrchestratorSystemPrompt(this.toolCalls);
    }
    buildInitialMessages(params, resolvedContext) {
        const userMessage = [
            params.input.message,
            "",
            "Requirements:",
            ...this._requirements.map((requirement) => `- [${requirement.id}] ${requirement.requirement}`),
        ].join("\n");
        if (resolvedContext && resolvedContext.length > 0) {
            const contextParts = this.buildContextContentParts(resolvedContext);
            if (contextParts.length > 0) {
                return [{
                        role: "user",
                        content: [
                            { type: "text", text: userMessage },
                            { type: "text", text: "\n\nAttached visual context:" },
                            ...contextParts,
                        ],
                    }];
            }
        }
        return [{ role: 'user', content: userMessage }];
    }
    async processSubmittedResults(submittedResults, _params, _context) {
        console.log(`[LiveExcelOrchestrator] Processing submitted results`);
        const results = [];
        for (const result of submittedResults.results) {
            results.push({
                requirementId: result.requirementId,
                relevantResults: result.relevantResults,
                rationale: result.rationale,
            });
        }
        return results;
    }
    buildFinalResult(results, threadId, tokensUsed, summary) {
        const base = super.buildFinalResult(results, threadId, tokensUsed, summary);
        const messages = this.threadManager.getMessages(threadId);
        const content = buildAssistantContent(summary, results);
        return {
            ...base,
            summary: summary || "",
            messages,
            content,
        };
    }
}
